import { Component, computed, input } from '@angular/core';
import { LucideIconComponent } from '../lucide-icon/lucide-icon.component';
import { TextComponent } from '../typography/text/text.component';
import { AppUser } from '../../../core/models/user.model';
import { getDriverCredentialIssues } from '../../../core/utils/driver-credentials';

/**
 * Non-blocking warning shown when dispatch picks a driver whose CDL or DOT
 * medical card is expired or lapses before the load date.
 * Renders nothing when the driver's credentials are fine.
 */
@Component({
  selector: 'app-driver-credential-warning',
  standalone: true,
  imports: [LucideIconComponent, TextComponent],
  template: `
    @if (issues().length > 0) {
      <div class="flex items-start gap-2 p-3 rounded-lg bg-surface-high border border-warning text-warning">
        <lucide-icon name="TriangleAlert" class="w-4 h-4 mt-0.5 shrink-0"></lucide-icon>
        <div class="flex flex-col gap-0.5">
          <app-text size="xs" weight="semibold" color="warning">
            {{ driverName() }} — credential warning
          </app-text>
          @for (issue of issues(); track issue.kind) {
            <app-text size="xs" color="warning">{{ issue.message }}</app-text>
          }
          <app-text size="xs" color="muted">You can still assign this driver. Ask them to scan their renewed card in the driver app.</app-text>
        </div>
      </div>
    }
  `,
})
export class DriverCredentialWarningComponent {
  readonly driver = input<AppUser | null | undefined>(null);
  /** Last scheduled date of the load (credentials must be valid through it). */
  readonly loadDate = input<Date | null>(null);

  protected readonly issues = computed(() => getDriverCredentialIssues(this.driver(), this.loadDate()));
  protected readonly driverName = computed(() => {
    const d = this.driver();
    return d?.fullName || `${d?.firstName || ''} ${d?.lastName || ''}`.trim() || 'This driver';
  });
}
